// client/src/components/ActionConfirmationModal.tsx
import { AlertTriangle, Info, X, Loader2 } from "lucide-react";

interface ActionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  itemName?: string;
  additionalInfo?: string;
}

export default function ActionConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = 'warning',
  isLoading = false,
  itemName,
  additionalInfo
}: ActionConfirmationModalProps) {
  
  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: <AlertTriangle className="w-12 h-12 text-red-500" />,
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          confirmBtn: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-12 h-12 text-blue-500" />,
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          confirmBtn: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
      case 'info':
        return {
          icon: <Info className="w-12 h-12 text-blue-500" />,
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          confirmBtn: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md rounded-xl shadow-2xl bg-white dark:bg-[#333436] animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-2 rounded-lg transition hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-8">
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className={`p-4 rounded-full mb-6 ${styles.iconBg}`}>
              {styles.icon}
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
              {title}
            </h3>

            {/* Message */}
            <p className="text-base text-gray-600 dark:text-gray-300 mb-4">
              {message}
            </p>

            {/* Item Name (if provided) */}
            {itemName && (
              <div className="w-full p-4 mb-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  "{itemName}"
                </p>
              </div>
            )}

            {/* Additional Info */}
            {additionalInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {additionalInfo}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 w-full mt-2">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-6 py-3 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${styles.confirmBtn}`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}